import { useTranslation } from "react-i18next";

import { useMemo } from "react";

import { faCheckCircle, faCircle } from "@fortawesome/pro-regular-svg-icons";

import { useSearchParams } from "react-router-dom";

import gridBlocks from "@/assets/images/landing-page/grid blocks.png";

import { ToDoPriority } from "@/pages/Private/pages/ToDo/ToDoList/enum/ToDoPriority.enum";
import { ToDoStatus } from "@/pages/Private/pages/ToDo/ToDoList/enum/ToDoStatus.enum";

import { Icon } from "@/components";

import { classNames } from "@/utils/classNames";

import { CompanyToDoProgressInfoDto } from "@/pages/Private/pages/ToDo/ToDoList/schema/to-do";

import MaconiaAppLogo from "@/assets/images/maconia-logo.png";
import AppLogo from "@/assets/images/favicon.png";
import qrCode from "@/assets/images/Bild.png";

import { useGetTodosForDownloadQuery } from "../../redux/questionnaire/questionnaire.api";

export default function Download() {
	const { t } = useTranslation();
	const ts = (key: string, options = {}) => t(`questionnaire.finalize.${key}`, options);

	const [searchParams] = useSearchParams();
	const token = searchParams.get("token");

	const { data: toDos } = useGetTodosForDownloadQuery({ token: token ?? "" });

	const priorityToNumber = (priority: ToDoPriority) => {
		switch (priority) {
			case ToDoPriority.LOW:
				return 1;
			case ToDoPriority.MEDIUM:
				return 2;
		}

		return 3;
	};

	const filteredToDos = useMemo(() => {
		const myFilteredToDos = [...(toDos || [])]?.filter((toDo) => toDo.toDo?.id !== undefined);

		return myFilteredToDos
			? myFilteredToDos.sort(
					(a, b) =>
						priorityToNumber(b.toDo?.priority ?? ToDoPriority.LOW) -
						priorityToNumber(a.toDo?.priority ?? ToDoPriority.LOW)
			  )
			: [];
	}, [toDos]);

	const todosByCategory = useMemo(() => {
		return filteredToDos.reduce((acc, toDo) => {
			if (toDo.toDo?.tag?.name) {
				acc[toDo.toDo?.tag?.name] = [...(acc[toDo.toDo?.tag?.name] || []), toDo];
			}

			return acc;
		}, {} as Record<string, CompanyToDoProgressInfoDto[]>);
	}, [filteredToDos]);

	return (
		<div className="print-root">
			<div className="print-first-page">
				<section className=" relative w-full bg-surface py-3 sm:py-6 px-2 flex justify-center overflow-hidden">
					<div
						className="absolute inset-0 bg-no-repeat bg-cover bg-center z-0 opacity-[0.04]"
						style={{ backgroundImage: `url(${gridBlocks})` }}
					/>
					<div className="relative z-10 flex flex-col md:flex-row justify-center items-center max-w-[1500px] w-full">
						<div className="flex flex-col max-w-[694px] w-full">
							<div className="flex flex-row">
								<div className="flex gap-3 items-center justify-center w-fit">
									<img
										alt="logo"
										className="w-fit h-[36px] bg-white rounded-md p-1 cursor-pointer z-40 text-primary-blue"
										src={`${AppLogo}`}
									/>

									<img
										alt="MaconiaAppLogo"
										className=" h-[36px] bg-white p-2  rounded-md w-fit cursor-pointer z-40"
										src={`${MaconiaAppLogo}`}
									/>
								</div>
								<div className="flex flex-col gap-3 items-center ml-4">
									<h1 className="font-urbanist font-bold text-ink text-lg md:text-md leading-[120%]">
										{ts("title")}
									</h1>
								</div>
							</div>
							<p className="font-urbanist font-medium text-subtle text-center text-sm md:text-base leading-[140%]">
								{ts("subtitle")}
							</p>
						</div>
					</div>
				</section>
				<div className="px-4 py-4 mb-0">
					<div className=" mx-auto text-xxs/3 w-full h-full flex flex-col items-start justify-center gap-2">
						<h2 className="font-urbanist font-bold text-primary-blue-dark text-sm leading-[120%]">
							{ts("introduction.title")}
						</h2>
						<p>
							Die novellierte europäische Richtlinie zur Netz- und Informationssicherheit (NIS-2)
							verpflichtet die EU-Mitgliedstaaten zur Schaffung gesetzlicher Rahmenbedingungen, um
							wesentliche und wichtige Einrichtungen vor Störungen durch Cybervorfälle zu schützen.
							In Deutschland erfolgte die Umsetzung der NIS-2-Richtlinie durch das NIS2UmsuCG,
							welches am 6. Dezember 2025 in Kraft trat und dabei insbesondere das BSI-Gesetz
							umfassend novellierte.
						</p>
						<p>
							Die Richtlinie definiert zehn Kategorien für das Cyber-Risikomanagement sowie strenge
							Meldepflichten bei Vorfällen. Betroffene Unternehmen müssen geeignete Maßnahmen sowie
							Technologien implementieren und die Einhaltung der gesetzlichen Vorgaben nachweisen.
							Die Geschäftsführung ist dabei persönlich für die Überwachung verantwortlich und
							haftet unmittelbar bei Verstößen.
						</p>
						<p>
							Konkrete technische Maßnahmen gibt die Richtlinie nicht vor. Unsere Roadmap
							präsentiert Ihnen einen Überblick über die grundlegenden Anforderungen und
							Sicherheitsziele, die hinter den Maßnahmen und Verpflichtungen stehen. Hierfür hat die
							MACONIA etablierte Branchenstandards wie die ISO/IEC 27001 und den BSI IT-Grundschutz
							als Basis herangezogen, um Ihnen eine maßgeschneiderte und umfangreiche Compliance zu
							gewährleisten und Sie optimal auf kommende Audits vorzubereiten.
						</p>
						<p>
							Bereit für NIS-2? Bei Fragen oder Unterstützungsbedarf stehen wir Ihnen jederzeit
							unter der Telefonnummer
							<a className="ml-0.5 font-semibold" href="tel:+49303001493170">
								+49 30 300 149 3170
							</a>
							, via E-Mail unter
							<a
								className="mx-0.5 font-semibold"
								href="mailto:kontakt@maconia.de"
								rel="noreferrer"
								target="_blank"
							>
								kontakt@maconia.de
							</a>
							oder über unser
							<a
								className="mx-0.5 font-semibold"
								href="https://maconia.de/kontakt-anfrage"
								rel="noreferrer"
								target="_blank"
							>
								Kontaktformular
							</a>
							zur Verfügung. Wir freuen uns darauf, Sie sicher ans Ziel zu bringen!
						</p>
					</div>
				</div>
				<div className="px-4 mb-4">
					<div className="flex flex-col text-sm w-full h-full flex flex-col items-start justify-center gap-2">
						<div className=" text-xxs font-semibold flex flex-row items-start justify-center gap-2 items-center w-full">
							<div className="rounded-lg bg-secondary-blue-dark text-white font-semibold w-[150px] min-w-[150px] whitespace-nowrap no-wrap  p-2">
								ANFORDERUNGSGRUPPEN
							</div>
							<div className="rounded-lg bg-secondary-blue-dark text-white font-semibold p-2 w-[calc(100%-150px)]">
								MAßNAHMEN
							</div>
						</div>
						{Object.keys(todosByCategory).map((category) => (
							<div
								key={`${category}-container`}
								className="flex flex-row break-inside-avoid items-start justify-start gap-2 items-center w-full"
							>
								<div className="w-[150px] min-w-[150px] self-start text-xxs whitespace-wrap wrap-normal  p-2">
									{category}
								</div>
								<div
									key={`${category}-container`}
									className="flex flex-row  flex-wrap items-start justify-start gap-2 items-center w-full"
								>
									{todosByCategory[category].map((toDo) => (
										<div
											key={`${category}-${toDo.id}`}
											className="rounded-lg bg-surface text-primary-blue-dark border border-primary-gray-lightest p-2 max-w-[150px] w-full h-[64px]"
										>
											<p className="text-xxs/3 font-normal line-clamp-4">{toDo.toDo?.title}</p>
										</div>
									))}
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
			<div className="print-paginated ">
				<div className="px-4 mb-4 print-content relative">
					<div className=" w-full flex items-start justify-center gap-4 ">
						<div className="w-full h-full gap-6 flex flex-col ">
							{filteredToDos &&
								filteredToDos.length > 0 &&
								filteredToDos.map((toDo, index) => (
									<div key={toDo.id} className="h-full w-full sub-task ">
										<div className="w-full mt-4">{renderToDo(toDo, index + 1)}</div>
									</div>
								))}
							<footer className="w-full flex mt-4 print-footer gap-2">
								<img className="w-auto h-[50px]" src={MaconiaAppLogo} />

								<p className="text-xs self-center">
									Kurfürstendamm 11, 10719 Berlin - HRB 211946 B, Amtsgericht Berlin
								</p>

								<a
									className="text-xs self-center col-span-2"
									href="https://www.maconia.de"
									rel="noreferrer"
									target="_blank"
								>
									www.maconia.de
								</a>

								<img className="w-auto h-[80px]" src={qrCode} />
							</footer>
						</div>
					</div>
				</div>
			</div>
		</div>
	);

	function renderToDo(toDo: CompanyToDoProgressInfoDto, itemNumber: number) {
		return (
			<div key={toDo.id} className={classNames("w-full h-fit mb-4")}>
				<div className="w-full h-full bg-white rounded-lg relative">
					<div className="flex flex-row items-center gap-2">
						<div className="text-xs/3 bg-primary-blue-dark text-white p-3 w-3 h-3 flex items-center justify-center rounded-full font-bold">
							{itemNumber}
						</div>
						<span
							className={`text-xs/3 font-bold flex items-center gap-2 ${
								toDo.status === ToDoStatus.DONE ? "text-status-success" : ""
							}`}
						>
							{toDo.status === ToDoStatus.DONE ? "Umgesetzt: " : ""}
							{toDo.toDo?.title}
						</span>
					</div>
					<div className="border-l border-border pl-6 ml-[10px] mt-2 pt-0">
						<div className="  flex flex-col bg-brand-white p-2 gap-2 rounded-lg mb-2">
							<span className="text-xs/3 font-semibold">In aller Kürze</span>
							<div
								dangerouslySetInnerHTML={{ __html: toDo.toDo?.description ?? "" }}
								className={classNames(
									" text-primary-gray cursor-pointer !font-normal !text-xxs/3 mt-1 mb-0"
								)}
							/>
						</div>

						{toDo.toDo?.subTasks && toDo.toDo?.subTasks?.length > 0 ? (
							<div className="flex flex-col gap-1 pt-1 mt-1">
								{toDo.toDo?.subTasks?.map((subTask, index) => (
									<div
										key={subTask.id}
										className=" break-inside-avoid flex items-start justify-start gap-1 flex-col  pb-2 rounded-lg"
									>
										<div
											className={`text-xs/3 flex items-center justify-center font-semibold ${
												(toDo.subTasks &&
													subTask.id &&
													toDo.subTasks[subTask.id] === ToDoStatus.DONE) ||
												toDo.status === ToDoStatus.DONE
													? "text-status-success"
													: "text-primary-blue-dark"
											}`}
										>
											{(toDo.subTasks &&
												subTask.id &&
												toDo.subTasks[subTask.id] === ToDoStatus.DONE) ||
											toDo.status === ToDoStatus.DONE ? (
												<Icon
													className="text-status-success w-3 h-3 mt-0 mr-2"
													icon={faCheckCircle}
												/>
											) : (
												<Icon
													className="text-primary-gray-lighter w-3 h-3 mt-0 mr-2"
													icon={faCircle}
												/>
											)}
											{itemNumber}.{index + 1} {subTask.title}
										</div>
										<div className="flex items-start justify-start text-left gap-0 flex-col">
											<div
												dangerouslySetInnerHTML={{ __html: subTask.description ?? "" }}
												className="text-primary-blue-dark flex-col font-normal text-xxs/3 flex items-start gap-1"
											/>
										</div>
									</div>
								))}
							</div>
						) : (
							<div></div>
						)}
					</div>
				</div>
			</div>
		);
	}
}
